import React, { useState, useEffect } from "react";
import InstituteEnrollProgramCard from "../../components/institute/InstituteEnrollProgramCard";
import InstituteEnrollCourseCard from "../../components/institute/InstituteEnrollCourseCard";
import SkeletonLoading from "../../components/SkeletonLoading";
import PageHeader from "../../components/PageHeader";

const InstituteEnrollProgram = () => {
  const [programsData, setProgramsData] = useState(null);
  const [expandedProgramData, setExpandedProgramData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [error, setError] = useState("");
  const InstituteId = sessionStorage.getItem("instituteId") || sessionStorage.getItem("studentInstituteId");
  const role = sessionStorage.getItem("role");
  const [expandedProgram, setExpandedProgram] = useState(null);

  useEffect(() => {
    const token = process.env.REACT_APP_API_TOKEN;
    const LMS = process.env.REACT_APP_API_LMS;

    if (!token) {
      setError("Authentication token not found. Please try logging in again.");
      setLoading(false);
      return;
    }

    async function getPrograms() {
      try {
        // Get user_id from sessionStorage or use a default value
        const userId = sessionStorage.getItem("id");
        const instituteId = sessionStorage.getItem("instituteId") || sessionStorage.getItem("studentInstituteId") ;
        
        if (!userId) {
          throw new Error("User ID is missing. Please log in again.");
        }

        // Build query params; include role=Teacher only if the role is Teacher
        const params = new URLSearchParams({
          user_id: userId,
          institute_id: instituteId
        });
        if (role === "Teacher") {
          params.append("role_name", "Teacher");
        }

        // Use the correct API endpoint as provided
        const apiUrl = `${LMS}/api/get-institute-program?${params.toString()}`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Unable to load programs. Please try again later. (${response.status})`);
        }

        const data = await response.json();
        
        // Check if the response has the expected structure
        if (data.status && data.programs) {
          setProgramsData(data);
        } else {
          throw new Error("Invalid response format from server.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    getPrograms();
  }, []);

  const fetchProgramDetailsWithGraph = async (programId) => {
    const token = process.env.REACT_APP_API_TOKEN;
    const LMS = process.env.REACT_APP_API_LMS;
    
    try {
      setExpandedLoading(true);
      setError("");
      
      // Since the programs already contain course data, we can use that directly
      // Or if you need to fetch additional details, use the appropriate API
      const program = programsData?.programs?.find(p => p.program_id === programId);
      
      if (program) {
        // Use the existing program data which already includes courses
        setExpandedProgramData(program);
      } else {
        throw new Error("Program not found.");
      }
    } catch (err) {
      console.error("Error fetching program details:", err);
      setError(err.message);
    } finally {
      setExpandedLoading(false);
    }
  };

  const handleViewMore = async (programId) => {
    if (expandedProgram === programId) {
      setExpandedProgram(null);
      setExpandedProgramData(null);
    } else {
      setExpandedProgram(programId);
      setExpandedProgramData(null);
      await fetchProgramDetailsWithGraph(programId);
    }
  };

  // Get programs array from the API response
  const programsArray = programsData?.programs || [];
  
  const getExpandedProgramDetails = () => {
    if (!expandedProgramData || !expandedProgram) return null;
    return expandedProgramData;
  };

  const expandedProgramDetails = getExpandedProgramDetails();

  if (error) {
    return (
      <div className="row">
        <div className="col-12">
          <PageHeader title="Program Assigned" 
            breadcrumbs={[
              { label: "Dashboard", path: "/dashboard" },
              { label: "Program Assigned", active: true }
            ]}
          />
        </div>
        <div className="col-12">
          <div className="error-container text-center p-4">
            <div className="error-icon mb-3">
              <i className="ri-error-warning-line text-danger" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5 className="text-danger mb-2">Oops! Something went wrong</h5>
            <p className="text-muted">{error}</p>
            <button 
              className="btn btn-soft-primary mt-2"
              onClick={() => window.location.reload()}
            >
              <i className="ri-refresh-line me-1"></i> Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <PageHeader title="Program Assigned" 
          breadcrumbs={[
            { label: "Dashboard", path: "/dashboard" },
            { label: "Program Assigned", active: true }
          ]}
        />
      </div>

      <div className="col-12">
        <div className="mb-4">
          <div>
            {loading ? (
              <>
                <SkeletonLoading count={3} />
                <SkeletonLoading height="80px" width="100%" />
              </>
            ) : (
              <>
                <InstituteEnrollProgramCard 
                  programs={programsArray} 
                  onViewMore={handleViewMore}
                  expandedProgram={expandedProgram}
                  hideImportEnroll={role === "Teacher"}
                />
                
                {expandedProgram && (
                  <div className="collapse show" id={`collapseProgram-${expandedProgram}`}>
                    {expandedLoading ? (
                      <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading program details...</p>
                      </div>
                    ) : expandedProgramDetails ? (
                      <>
                        <InstituteEnrollCourseCard 
                          courses={expandedProgramDetails.courses || []}
                          programId={expandedProgram}
                          programData={expandedProgramDetails}
                          hideImportEnroll={role === "Teacher"}
                        />
                      </>
                    ) : (
                      <div className="text-center p-4">
                        <p className="text-muted">No details available for this program.</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Empty State */}
        {!loading && programsArray.length === 0 && (
          <div className="text-center py-5 border rounded-4">
            {/* <div className="mb-4">
              <i className="ri-book-2-line display-1 text-muted"></i>
            </div> */}
            <h5 className="mb-3">No Programs Found</h5>
            <p className="text-muted mb-4">
              No programs are currently available for institute enrollment. Check back later!
            </p>
            {/* <button 
              className="btn btn-primary"
              onClick={() => window.location.href = '/programs'}
            >
              Browse Programs <i className="ri-arrow-right-line align-bottom"></i>
            </button> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstituteEnrollProgram;
